import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from "@apollo/client";
import {
    Box,
    Button,
    Checkbox,
    Container,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Chip,
    InputAdornment,
    CircularProgress,
    Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { CREATE_USER_GROUP, LIST_GROUPS, DELETE_USER_GROUP } from "../../graphql/admin-requests";
import { computeItemUpdateWithId } from "../../utils/reducers"
import { useSnackbar } from 'notistack';



function UserGroups({ onFinish, user }) {

    const { enqueueSnackbar } = useSnackbar();

    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // State for filtering
    const [searchText, setSearchText] = useState('');

    // State for groups and selections
    const [allGroups, setAllGroups] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [initialUserGroupsUids, setInitialUserGroupsUids] = useState([]);
    const [selectedGroupUids, setSelectedGroupUids] = useState(new Set());
    const [isInitialized, setIsInitialized] = useState(false);

    // Compute filter for GraphQL query
    const groupFilter = useMemo(() => {
        if (!searchText.trim()) return undefined;
        return { name: { includesInsensitive: searchText.trim() } };
    }, [searchText]);

    const [createUserGroup, { loading: creatingGroup }] = useMutation(CREATE_USER_GROUP, {
        onCompleted: (data) => {
            console.log("1 group ajouté", data);
        },
        onError: (error) => {
            enqueueSnackbar("Impossible d'ajouter le groupe", { variant: 'error' });
        },
    });

    const [deleteUserGroup, { loading: deletingGroup }] = useMutation(DELETE_USER_GROUP, {
        onCompleted: (data) => {
            console.log("1 group supprimé", data);
        },
        onError: (error) => {
            enqueueSnackbar("Impossible de supprimer le groupe", { variant: 'error' });
        },
    });

    // Retrieve groups with pagination and filtering
    const { loading } = useQuery(LIST_GROUPS, {
        variables: {
            first: rowsPerPage,
            offset: page * rowsPerPage,
            filter: groupFilter
        },
        onCompleted: (data) => {
            if (!isInitialized) {
                const initialGroups = user.userGroupsByUserUid.nodes.map((ug) => ug.groupByGroupUid.uid);
                setInitialUserGroupsUids(initialGroups);
                setSelectedGroupUids(new Set(initialGroups));
                setIsInitialized(true);
            }
            setAllGroups([...data.allGroups.nodes].sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true, sensitivity: 'base' })));
            setTotalCount(data.allGroups.totalCount);
        },
        onError: (error) => {
            console.error("Error requesting list of groups", error);
            enqueueSnackbar("Erreur lors du chargement des groupes", { variant: 'error' });
        },
        fetchPolicy: 'cache-and-network'
    });

    // Reset to first page when search changes
    useEffect(() => {
        setPage(0);
    }, [searchText]);

    const handleCheckBox = (groupUid) => {
        const newSelected = new Set(selectedGroupUids);
        if (newSelected.has(groupUid)) {
            newSelected.delete(groupUid);
        } else {
            newSelected.add(groupUid);
        }
        setSelectedGroupUids(newSelected);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelected = new Set(selectedGroupUids);
            allGroups.forEach(group => newSelected.add(group.uid));
            setSelectedGroupUids(newSelected);
        } else {
            const newSelected = new Set(selectedGroupUids);
            allGroups.forEach(group => newSelected.delete(group.uid));
            setSelectedGroupUids(newSelected);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
    };

    async function handleValidateGroup(event) {
        event.preventDefault();
        const selectedGroupUidsArray = Array.from(selectedGroupUids);
        const { toDelete: groupsToDelete, toAdd: groupsToAdd } = computeItemUpdateWithId(
            initialUserGroupsUids,
            selectedGroupUidsArray
        );
        const userUid = user.uid;

        try {
            await Promise.all(groupsToDelete.map(async groupUid =>
                await deleteUserGroup({ variables: { userUid, groupUid } })));

            await Promise.all(groupsToAdd.map(async groupUid =>
                await createUserGroup({ variables: { userUid, groupUid } })));

            enqueueSnackbar("Groupes mis à jour", { variant: 'success' });
            onFinish();
        } catch (error) {
            enqueueSnackbar("Erreur lors de la mise à jour des groupes", { variant: 'error' });
        }
    }

    const isSelected = (groupUid) => selectedGroupUids.has(groupUid);
    const isIndeterminate = allGroups.length > 0 && 
        allGroups.some(g => selectedGroupUids.has(g.uid)) && 
        !allGroups.every(g => selectedGroupUids.has(g.uid));
    const isAllSelected = allGroups.length > 0 && allGroups.every(g => selectedGroupUids.has(g.uid));

    return (
        <Container component="main" maxWidth="md">
            <Box sx={{ py: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h5" component="h2">
                        Groupes de {user.firstName} {user.lastName}
                    </Typography>
                    <Chip 
                        label={`${selectedGroupUids.size} sélectionné${selectedGroupUids.size > 1 ? 's' : ''}`} 
                        color="primary" 
                        variant="outlined"
                    />
                </Stack>
                <Divider sx={{ mb: 3 }} />

                {/* Search Field */}
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Rechercher un groupe..."
                    value={searchText}
                    onChange={handleSearchChange}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Groups Table */}
                <Paper elevation={2}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={isIndeterminate}
                                            checked={isAllSelected}
                                            onChange={handleSelectAll}
                                            disabled={loading || allGroups.length === 0}
                                        />
                                    </TableCell>
                                    <TableCell><strong>Nom du groupe</strong></TableCell>
                                    <TableCell align="right"><strong>Eleves</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : allGroups.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">
                                                Aucun groupe trouvé
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    allGroups.map((group) => {
                                        const isItemSelected = isSelected(group.uid);
                                        return (
                                            <TableRow
                                                hover
                                                onClick={() => handleCheckBox(group.uid)}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={group.uid}
                                                selected={isItemSelected}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isItemSelected}
                                                        color="primary"
                                                    />
                                                </TableCell>
                                                <TableCell>{group.name}</TableCell>
                                                <TableCell align="right">
                                                    {group.userGroupsByGroupUid.totalCount}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Lignes par page:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
                    />
                </Paper>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button 
                        variant="outlined" 
                        onClick={onFinish}
                        disabled={creatingGroup || deletingGroup}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={creatingGroup || deletingGroup || loading}
                        onClick={handleValidateGroup}
                    >
                        {creatingGroup || deletingGroup ? 'Enregistrement...' : 'Valider les groupes'}
                    </Button>
                </Box>
            </Box>
        </Container>
    );

}
export default UserGroups;